import { combineSlices, createSelector } from '@reduxjs/toolkit'
import { RootState } from '../store'
import { roomSessionSlice } from './roomSession'
import { executedAiFeaturesSlice } from './executedAiFeatures'
import { aiFeaturesSlice, selectAiFeatures } from './aiFeatures'
import { aiResultsSlice, selectAiResults, selectAiResultsLoading } from './aiResults'
import { aiFeaturePresetsSlice } from './aiFeaturePresets'
import { meetingTemplatesSlice } from './meetingTemplates'
import { meetingSupportSlice } from './meetingSupport'
import { displaySlice } from './display'
import { userSlice } from './user'
import { inputParametersHierarchySlice } from './inputParametersHierarchy'
import { formsSlice } from './forms'
import { clientProfileSlice } from './clientProfile'
import { contactUpdateSlice } from './contactUpdate'
import { selectTasks, selectTasksLoading } from '../task/task'
import { hasTenMinutesPassed } from '../utils'
import { TASK_PRESET_IDS } from '../../views/MeetingSummary/CRM/PresetMappings'

export default combineSlices(
  userSlice,
  roomSessionSlice,
  executedAiFeaturesSlice,
  aiFeaturesSlice,
  aiResultsSlice,
  aiFeaturePresetsSlice,
  formsSlice,
  clientProfileSlice,
  meetingTemplatesSlice,
  inputParametersHierarchySlice,
  displaySlice,
  contactUpdateSlice,
  { meetingSupport: meetingSupportSlice },
)

export const selectMeetingSummaryLoading = (state: RootState) => {
  const {
    meetingSummary: {
      user,
      roomSession,
      executedAiFeatures,
      aiFeatures,
      aiResults,
      aiFeaturePresets,
      meetingTemplates,
      inputParametersHierarchy,
      meetingSupport,
    },
  } = state

  return (
    user.isCurrentUserLoading ||
    roomSession.isLoading ||
    executedAiFeatures.isLoading ||
    aiFeatures.isLoading ||
    aiResults.isLoading ||
    aiFeaturePresets.isLoading ||
    meetingTemplates.isLoading ||
    inputParametersHierarchy.isLoading ||
    meetingSupport.comments.isLoading
  )
}

export const selectShouldPollMeetingTasks = createSelector(
  [selectTasksLoading, selectTasks, selectAiResultsLoading, selectAiResults, selectAiFeatures],
  (tasksLoading, tasks, aiResultsLoading, aiResults, aiFeatures) => {
    if (tasksLoading || aiResultsLoading || tasks.length || !aiResults.length || !aiFeatures.length) {
      return false
    }

    const taskAiFeature = aiFeatures.find(({ preset }) => preset && TASK_PRESET_IDS.includes(preset.id))
    if (!taskAiFeature) {
      return false
    }

    const taskAiResult = aiResults.find(({ aiFeatureId }) => aiFeatureId === taskAiFeature.id)
    if (!taskAiResult) {
      return false
    }

    const { result: aiFeatureResult } = taskAiResult

    const isTaskResultEmpty =
      !aiFeatureResult || (aiFeatureResult.type === 'structuredSummary' && Array.isArray(aiFeatureResult.result) && !aiFeatureResult.result.length)

    if (isTaskResultEmpty) {
      return false
    }

    return !hasTenMinutesPassed(taskAiResult.createdAt)
  },
)
